import React, { lazy, Suspense, useEffect, useState } from "react";
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, Typography, CircularProgress, IconButton, useMediaQuery, useTheme } from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LoginIcon from "@mui/icons-material/Login";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExploreIcon from "@mui/icons-material/Explore";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupIcon from "@mui/icons-material/Group";
import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuIcon from '@mui/icons-material/Menu';
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import InstagramIcon from "@mui/icons-material/Instagram";
import ProntuarioPage from "./pages/ProntuarioPage";

const PetForm = lazy(() => import("./pages/PetForm"));
const PetList = lazy(() => import("./pages/PetList"));
const AdoptedPetsPage = lazy(() => import("./pages/AdoptedPetsPage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AdoptionManagementPage = lazy(() => import("./pages/AdoptionManagementPage"));
const GuiaPetFriendly = lazy(() => import("./pages/GuiaPetFriendly"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const OngsPage = lazy(() => import("./pages/OngsPage"));
const SobreAcolhePetPage = lazy(() => import("./pages/SobreAcolhePetPage"));

const drawerWidth = 240;
const drawerCollapsedWidth = 72;

function AppContent() {
  const location = useLocation();
  const { user, logout, podeVerProntuario, podeGerenciarAdocao, podeGerenciarUsuarios } = useAuth();
  const [menuAberto, setMenuAberto] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const nomeUsuarioSidebar = String(user?.nomeCompleto || user?.nome || "").trim();
  const perfilUsuario = String(user?.perfil || "").trim().toUpperCase();
  const perfilUsuarioLabel =
    perfilUsuario === "ADMIN"
      ? "Admin"
      : perfilUsuario === "VETERINARIO"
        ? "Veterinario"
        : perfilUsuario === "SUPERVISOR"
          ? "Supervisor"
          : perfilUsuario === "CADASTRADOR"
            ? "Cadastrador"
            : "";

  const drawerWidthCurrent = isMobile ? drawerWidth : menuAberto ? drawerWidth : drawerCollapsedWidth;
  const mostrarLabels = isMobile || menuAberto;

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleCloseMobileDrawer = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    handleCloseMobileDrawer();
  };

  useEffect(() => {
    const preloadRotas = () => {
      import("./pages/GuiaPetFriendly");
      import("./pages/AdoptedPetsPage");
      import("./pages/EventsPage");
      import("./pages/OngsPage");
      import("./pages/SobreAcolhePetPage");

      if (user) {
        import("./pages/AdoptionManagementPage");
        import("./pages/PetForm");
      }
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadRotas, { timeout: 2000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadRotas, 1200);
    return () => window.clearTimeout(timeoutId);
  }, [user]);

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleCloseMobileDrawer : undefined}
        onMouseEnter={!isMobile ? () => setMenuAberto(true) : undefined}
        onMouseLeave={!isMobile ? () => setMenuAberto(false) : undefined}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidthCurrent,
          flexShrink: 0,
          transition: "width 0.25s",
          [`& .MuiDrawer-paper`]: {
            width: drawerWidthCurrent,
            backgroundColor: "#f4f6f8",
            boxSizing: "border-box",
            overflowX: "hidden",
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            justifyContent: mostrarLabels ? "flex-start" : "center",
            alignItems: "center",
            px: mostrarLabels ? 2 : 0,
            minHeight: 72,
          }}
        >
          <Box
            component={Link}
            to="/"
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: mostrarLabels ? "flex-start" : "center",
              width: "100%",
              minHeight: 40,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Box
              component="img"
              src="/logo_com_nome_reduzida.png"
              alt="Logo Acolhe Pet - SSA"
              sx={{
                width: 40,
                height: 40,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
                mr: mostrarLabels ? 1.25 : 0,
              }}
            />
            {mostrarLabels && (
              <Box sx={{ display: "flex", flexDirection: "column", ml: 1, mt: 2 }}>
                <Typography
                  variant="h7"
                  sx={{ fontWeight: "bold", color: "#2e7d32", lineHeight: 1.1 }}
                >
                  Acolhe Pet - SSA
                </Typography>
                {nomeUsuarioSidebar && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#2e7d32", fontWeight: 600, mt: 0.5 }}
                  >
                    Olá, {nomeUsuarioSidebar}!
                  </Typography>
                )}
                {perfilUsuarioLabel && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#4f6f5a", fontWeight: 600, mt: 0.25 }}
                  >
                    {perfilUsuarioLabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Toolbar>
        <List onClick={isMobile ? handleCloseMobileDrawer : undefined}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              selected={location.pathname === "/"}
              sx={{
                justifyContent: mostrarLabels ? "flex-start" : "center",
                px: mostrarLabels ? 2 : 0,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mostrarLabels ? 40 : "auto",
                  justifyContent: "center",
                }}
              >
                <PetsIcon sx={{ color: '#a0522d' }} />
              </ListItemIcon>
              {mostrarLabels && <ListItemText primary="Ver pets disponíveis" />}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/adotados"
              selected={location.pathname === "/adotados"}
              sx={{
                justifyContent: mostrarLabels ? "flex-start" : "center",
                px: mostrarLabels ? 2 : 0,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mostrarLabels ? 40 : "auto",
                  justifyContent: "center",
                }}
              >
                <FavoriteIcon color="error" />
              </ListItemIcon>
              {mostrarLabels && <ListItemText primary="Pets adotados" />}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/guia-pet-friendly"
          selected={location.pathname === "/guia-pet-friendly"}
          sx={{
            justifyContent: mostrarLabels ? "flex-start" : "center",
            px: mostrarLabels ? 2 : 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: mostrarLabels ? 40 : "auto",
              justifyContent: "center",
            }}
          >
            {/* ExploreIcon combina muito com a ideia de Guia! */}
            <ExploreIcon sx={{ color: '#00796b' }} /> 
          </ListItemIcon>
          {mostrarLabels && <ListItemText primary="Guia Pet Friendly" />}
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/eventos"
          selected={location.pathname === "/eventos"}
          sx={{
            justifyContent: mostrarLabels ? "flex-start" : "center",
            px: mostrarLabels ? 2 : 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: mostrarLabels ? 40 : "auto",
              justifyContent: "center",
            }}
          >
            <EventNoteIcon sx={{ color: "#6d4c41" }} />
          </ListItemIcon>
          {mostrarLabels && <ListItemText primary="Eventos Pet" />}
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/ongs"
          selected={location.pathname === "/ongs"}
          sx={{
            justifyContent: mostrarLabels ? "flex-start" : "center",
            px: mostrarLabels ? 2 : 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: mostrarLabels ? 40 : "auto",
              justifyContent: "center",
            }}
          >
            <CampaignIcon sx={{ color: "#006064" }} />
          </ListItemIcon>
          {mostrarLabels && <ListItemText primary="Divulgacao de ONGs" />}
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/sobre-acolhe-pet"
          selected={location.pathname === "/sobre-acolhe-pet"}
          sx={{
            justifyContent: mostrarLabels ? "flex-start" : "center",
            px: mostrarLabels ? 2 : 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: mostrarLabels ? 40 : "auto",
              justifyContent: "center",
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#2e7d32" }} />
          </ListItemIcon>
          {mostrarLabels && <ListItemText primary="Sobre a Acolhe Pet" />}
        </ListItemButton>
      </ListItem>

        {/* ITENS PROTEGIDOS (Só aparecem se logado e com permissão) */}
                  {user && (
                    <>
                      {/* Cadastrar Pet: Todos os logados podem (Cadastrador, Vet, Admin, Supervisor) */}
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin/cadastrar" selected={location.pathname === "/admin/cadastrar"} sx={{ justifyContent: mostrarLabels ? "flex-start" : "center", px: mostrarLabels ? 2 : 0 }}>
                          <ListItemIcon sx={{ minWidth: mostrarLabels ? 40 : "auto", justifyContent: "center" }}><AddCircleOutlineIcon sx={{ color: '#006064' }} /></ListItemIcon>
                          {mostrarLabels && <ListItemText primary="Cadastrar / Editar pet" />}
                        </ListItemButton>
                      </ListItem>

                      {/* Gestão de Adoção: Só quem tem permissão pode ver */}
                      {podeGerenciarAdocao && (
                        <ListItem disablePadding>
                          <ListItemButton component={Link} to="/gestao-adocao" selected={location.pathname === "/gestao-adocao"} sx={{ justifyContent: mostrarLabels ? "flex-start" : "center", px: mostrarLabels ? 2 : 0 }}>
                            <ListItemIcon sx={{ minWidth: mostrarLabels ? 40 : "auto", justifyContent: "center" }}><AssignmentTurnedInIcon sx={{ color: '#2D6EB4' }} /></ListItemIcon>
                            {mostrarLabels && <ListItemText primary="Gestão da adoção" />}
                          </ListItemButton>
                        </ListItem>
                      )}

                     {/* ABA DE PRONTUÁRIO ATIVADA 🚀 */}
                      {podeVerProntuario && (
                        <ListItem disablePadding>
                          <ListItemButton 
                            component={Link} 
                            to="/prontuarios" 
                            selected={location.pathname === "/prontuarios"} 
                            sx={{ 
                              justifyContent: mostrarLabels ? "flex-start" : "center", 
                              px: mostrarLabels ? 2 : 0 
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: mostrarLabels ? 40 : "auto", justifyContent: "center" }}>
                              <MedicalInformationIcon sx={{ color: '#d32f2f' }} />
                            </ListItemIcon>
                            {mostrarLabels && <ListItemText primary="Saúde / Prontuário" />}
                          </ListItemButton>
                        </ListItem>
                      )}
                    </>
                  )}

                  {/* Gestão de Usuários: Apenas ADMIN vê */}
                  {user && podeGerenciarUsuarios && (
                    <ListItem disablePadding>
                      <ListItemButton component={Link} to="/admin/usuarios" selected={location.pathname === "/admin/usuarios"} sx={{ justifyContent: mostrarLabels ? "flex-start" : "center", px: mostrarLabels ? 2 : 0 }}>
                        <ListItemIcon sx={{ minWidth: mostrarLabels ? 40 : "auto", justifyContent: "center" }}><GroupIcon sx={{ color: '#2e7d32' }} /></ListItemIcon>
                        {mostrarLabels && <ListItemText primary="Gestão de usuários / ONGs" />}
                      </ListItemButton>
                    </ListItem>
                  )}

                  {/* Alterar Senha: Todos os logados veem */}
                  {user && (
                    <ListItem disablePadding>
                      <ListItemButton component={Link} to="/perfil/senha" selected={location.pathname === "/perfil/senha"} sx={{ justifyContent: mostrarLabels ? "flex-start" : "center", px: mostrarLabels ? 2 : 0 }}>
                        <ListItemIcon sx={{ minWidth: mostrarLabels ? 40 : "auto", justifyContent: "center" }}><LockResetIcon color="warning" /></ListItemIcon>
                        {mostrarLabels && <ListItemText primary="Alterar senha" />}
                      </ListItemButton>
                    </ListItem>
                  )}
        </List>



        <Divider />
        <List sx={{ mt: "auto" }}>
          <ListItem disablePadding>
            <ListItemButton
              component={user ? "div" : Link}
              to={user ? null : "/login"}
              onClick={user ? handleLogout : null}
              sx={{
                justifyContent: mostrarLabels ? "flex-start" : "center",
                px: mostrarLabels ? 2 : 0,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mostrarLabels ? 40 : "auto",
                  justifyContent: "center",
                }}
              >
                {user ? <LogoutIcon color="error" /> : <LoginIcon />}
              </ListItemIcon>
              {mostrarLabels && <ListItemText primary={user ? "Sair" : "Acesso ao sistema"} />}
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              aria-label="Abrir menu"
              sx={{ p: 1, bgcolor: '#f4f6f8', borderRadius: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: "#ffffff",
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'%3E%3Cg fill='%236c757d' fill-opacity='0.045'%3E%3Ccircle cx='70' cy='78' r='16'/%3E%3Ccircle cx='46' cy='54' r='9'/%3E%3Ccircle cx='63' cy='42' r='8'/%3E%3Ccircle cx='83' cy='42' r='8'/%3E%3Ccircle cx='100' cy='54' r='9'/%3E%3C/g%3E%3C/svg%3E\")",
            backgroundSize: "140px 140px",
            backgroundRepeat: "repeat",
          }}
        >
          <Suspense fallback={<Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress color="success" /></Box>}>
            <Routes>
              {/* ROTAS PÚBLICAS */}
              <Route path="/" element={<PetList />} />
              <Route path="/adotados" element={<AdoptedPetsPage />} />
              <Route path="/guia-pet-friendly" element={<GuiaPetFriendly />} />
              <Route path="/eventos" element={<EventsPage />} />
              <Route path="/ongs" element={<OngsPage />} />
              <Route path="/sobre-acolhe-pet" element={<SobreAcolhePetPage />} />
              <Route path="/login" element={<LoginPage />} />

              {/* ROTAS PROTEGIDAS POR LOGIN E PERMISSÃO */}
              <Route path="/admin/cadastrar" element={user ? <PetForm /> : <LoginPage />} />
              
              <Route 
                path="/gestao-adocao" 
                element={user && podeGerenciarAdocao ? <AdoptionManagementPage /> : <LoginPage />} 
              />
              
              <Route
                path="/admin/usuarios"
                element={user && podeGerenciarUsuarios ? <UserManagementPage /> : <LoginPage />}
              />
              <Route
                path="/prontuarios"
                element={user && podeVerProntuario ? <ProntuarioPage /> : <LoginPage />}
              />
              <Route path="/perfil/senha" element={user ? <ChangePasswordPage /> : <LoginPage />} />
            </Routes>
          </Suspense>
        </Box>
        {/* <Box
          component="footer"
          sx={{
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              Desenvolvido por Camila Marques -
            </Typography>
            <Box
              component="a"
              href="https://www.instagram.com/acolhepetssa/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram Acolhe Pet SSA"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: "#c2185b",
                textDecoration: "none",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <InstagramIcon fontSize="small" />
              @acolhepetssa
            </Box>
          </Box>
        </Box> */}
        <Box
  component="footer"
  sx={{
    py: 2,
    borderTop: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
  }}
>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      flexWrap: "wrap",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
      Desenvolvido por <strong>Camila Marques</strong>
    </Typography>

    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
      •
    </Typography>
   
    <Box
      component="a"
      href="/sobre-acolhe-pet" // Altere para a rota exata da sua aba, se for diferente
      sx={{
        color: "#2e7d32",
        textDecoration: "none",
        fontSize: 12,
        fontWeight: 600,
        "&:hover": { textDecoration: "underline", color: "#1b5e20" },
      }}
    >
      Sobre o Projeto 🐾
    </Box>

    <Typography variant="caption" color="text.secondary">
      •
    </Typography>

    {/* Seu link oficial do Instagram */}
    <Box
      component="a"
      href="https://www.instagram.com/acolhepetssa/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram Acolhe Pet SSA"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: "#c2185b",
        textDecoration: "none",
        fontSize: 12,
        fontWeight: 600,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <InstagramIcon sx={{ fontSize: 16 }} />
      @acolhepetssa
    </Box>
  </Box>
</Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;